import React, { useState } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { Checkbox } from '@/components/ui/checkbox'
import { Spinner } from '@/components/ui/spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ClientAddressDto,
  ClientDetailedDto,
  ClientEmailDto,
  ClientPhoneNumberDto,
} from 'api/Clients/Types/ClientDetailedDto'
import { UpdateClientStatus } from 'api/Clients/Types/UpdateClientStatus'
import { CountryDto } from 'api/Identity/Types/Country'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import AddButton from 'components/AddButton/AddButton'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { MainTemplate } from 'components/Templates/MainTemplate'
import { FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'
import { hasPermission } from 'utils/permissionsValidator'
import { v4 as uuid } from 'uuid'

import { ClientNavigation, ClientTab } from '../../Navigation/ClientNavigation'
import { AddressModal } from './AddressModal'
import { EmailModal } from './EmailModal'
import { PhoneModal } from './PhoneModal'

type ClientDto = ClientPhoneNumberDto | ClientEmailDto | ClientAddressDto | null
type ClientContactRecord = {
  id?: string
  tempId?: string
}

const isSameClientContactRecord = (left: ClientContactRecord, right: ClientContactRecord) => {
  if (left.id && right.id) {
    return left.id === right.id
  }

  if (left.tempId && right.tempId) {
    return left.tempId === right.tempId
  }

  return false
}

const getClientContactRowKey = (record: ClientContactRecord, fallback: string) =>
  record.id ?? record.tempId ?? fallback

const renderEmptyRow = (message: string, colSpan: number) => (
  <TableRow>
    <TableCell colSpan={colSpan} className='text-center text-muted-foreground'>
      {message}
    </TableCell>
  </TableRow>
)

export function instanceOfEmail(object: ClientDto): object is ClientEmailDto {
  return object !== null && 'address' in object
}

export function instanceOfPhone(object: ClientDto): object is ClientPhoneNumberDto {
  return object !== null && 'countryCode' in object
}

export function instanceOfAddress(object: ClientDto): object is ClientAddressDto {
  return object !== null && 'type' in object
}

const ClientContactFormUI: React.FC<FormikProps<ClientDetailsOtherFormValues> & OuterProps> = (
  props
) => {
  const [isEdit, setEdit] = useState<boolean>(false)
  const [addressModal, setAddressModal] = useState<ClientAddressDto | null>(null)
  const [phoneModal, setPhoneModal] = useState<ClientPhoneNumberDto | null>(null)
  const [emailModal, setEmailModal] = useState<ClientEmailDto | null>(null)
  const [confirmationModal, setConfirmationModal] = useState<ClientDto>(null)
  const {
    values,
    resetForm,
    handleSubmit,
    setSubmitting,
    setFieldValue,
    isSubmitting,
    countries,
    permissions,
    clientDetails,
    updateClientStatus,
  } = props

  const addresses = values.addresses ?? []
  const phoneNumbers = values.phoneNumbers ?? []
  const emails = values.emails ?? []
  const defaultCountry = countries[0]
    ? {
        id: countries[0].id,
        name: countries[0].name,
      }
    : null

  const cancel = () => {
    setEdit(false)
    resetForm()
  }

  const handleSubmitForm = () => {
    setSubmitting(true)
    setEdit(false)
    handleSubmit()
  }

  const upsertContactRecord = <T extends ClientContactRecord & { isPrimary: boolean }>(
    items: T[],
    nextItem: T
  ) => {
    const updatedItems = nextItem.isPrimary
      ? items.map((item) => ({ ...item, isPrimary: false }))
      : [...items]

    return [...updatedItems.filter((item) => !isSameClientContactRecord(item, nextItem)), nextItem]
  }

  const removeAndReassignPrimary = <T extends ClientContactRecord & { isPrimary: boolean }>(
    items: T[],
    itemToRemove: T
  ) =>
    items
      .filter((item) => !isSameClientContactRecord(item, itemToRemove))
      .map((item, index) => ({ ...item, isPrimary: index === 0 }))

  const handleChangeAddress = (data: ClientAddressDto) => {
    setFieldValue('addresses', upsertContactRecord(addresses, data))
    setAddressModal(null)
  }

  const handleChangePhone = (data: ClientPhoneNumberDto) => {
    setFieldValue('phoneNumbers', upsertContactRecord(phoneNumbers, data))
    setPhoneModal(null)
  }

  const handleChangeEmail = (data: ClientEmailDto) => {
    setFieldValue('emails', upsertContactRecord(emails, data))
    setEmailModal(null)
  }

  const handleDelete = () => {
    if (instanceOfAddress(confirmationModal)) {
      setFieldValue('addresses', removeAndReassignPrimary(addresses, confirmationModal))
    }

    if (instanceOfEmail(confirmationModal)) {
      setFieldValue('emails', removeAndReassignPrimary(emails, confirmationModal))
    }

    if (instanceOfPhone(confirmationModal)) {
      setFieldValue('phoneNumbers', removeAndReassignPrimary(phoneNumbers, confirmationModal))
    }

    setConfirmationModal(null)
  }

  const renderAddresses = () => {
    if (!addresses.length) {
      return renderEmptyRow('No addresses added yet.', 9)
    }

    return addresses.map((address, index) => (
      <TableRow key={getClientContactRowKey(address, `address-${index}`)}>
        <TableCell className='w-24 text-center'>
          <Checkbox checked={address.isPrimary} disabled className='mx-auto' />
        </TableCell>
        <TableCell>{address.street ?? ''}</TableCell>
        <TableCell>{address.streetHouseNo ?? 's/n'}</TableCell>
        <TableCell>{address.city ?? ''}</TableCell>
        <TableCell>{address.state ?? ''}</TableCell>
        <TableCell>{address.postalCode ?? ''}</TableCell>
        <TableCell>{address.country?.name ?? ''}</TableCell>
        <TableCell>{address.type}</TableCell>
        <TableCell className='w-16 text-right'>
          <MoreButton
            disabled={!isEdit || isSubmitting}
            items={[
              {
                name: 'Edit',
                onClick: () => setAddressModal(address),
              },
              {
                name: 'Delete',
                onClick: () => {
                  if (address.isPrimary) {
                    setConfirmationModal(address)
                    return
                  }

                  setFieldValue(
                    'addresses',
                    addresses.filter((item) => !isSameClientContactRecord(item, address))
                  )
                },
              },
            ]}
          />
        </TableCell>
      </TableRow>
    ))
  }

  const renderPhoneNumbers = () => {
    if (!phoneNumbers.length) {
      return renderEmptyRow('No phone numbers added yet.', 7)
    }

    return phoneNumbers.map((phone, index) => (
      <TableRow key={getClientContactRowKey(phone, `phone-${index}`)}>
        <TableCell className='w-24 text-center'>
          <Checkbox checked={phone.isPrimary} disabled className='mx-auto' />
        </TableCell>
        <TableCell>{phone.countryCode}</TableCell>
        <TableCell>{phone.number}</TableCell>
        <TableCell>{phone.type}</TableCell>
        <TableCell className='text-center'>
          <Checkbox checked={phone.isVerified} disabled className='mx-auto' />
        </TableCell>
        <TableCell className='text-center'>
          <Checkbox checked={phone.isUsedIn2FA} disabled className='mx-auto' />
        </TableCell>
        <TableCell className='w-16 text-right'>
          <MoreButton
            disabled={!isEdit || isSubmitting}
            items={[
              {
                name: 'Edit',
                onClick: () => {
                  setFieldValue('newCountryCode', phone.countryCode)
                  setPhoneModal(phone)
                },
              },
              {
                name: 'Delete',
                onClick: () => {
                  if (phone.isPrimary) {
                    setConfirmationModal(phone)
                    return
                  }

                  setFieldValue(
                    'phoneNumbers',
                    phoneNumbers.filter((item) => !isSameClientContactRecord(item, phone))
                  )
                },
              },
            ]}
          />
        </TableCell>
      </TableRow>
    ))
  }

  const renderEmails = () => {
    if (!emails.length) {
      return renderEmptyRow('No email addresses added yet.', 5)
    }

    return emails.map((email, index) => (
      <TableRow key={getClientContactRowKey(email, `email-${index}`)}>
        <TableCell className='w-24 text-center'>
          <Checkbox checked={email.isPrimary} disabled className='mx-auto' />
        </TableCell>
        <TableCell>{email.address}</TableCell>
        <TableCell>{email.emailType}</TableCell>
        <TableCell className='text-center'>
          <Checkbox checked={email.isUsedIn2FA} disabled className='mx-auto' />
        </TableCell>
        <TableCell className='w-16 text-right'>
          <MoreButton
            disabled={!isEdit || isSubmitting}
            items={[
              {
                name: 'Edit',
                onClick: () => setEmailModal(email),
              },
              {
                name: 'Delete',
                onClick: () => {
                  if (email.isPrimary) {
                    setConfirmationModal(email)
                    return
                  }

                  setFieldValue(
                    'emails',
                    emails.filter((item) => !isSameClientContactRecord(item, email))
                  )
                },
              },
            ]}
          />
        </TableCell>
      </TableRow>
    ))
  }

  return (
    <React.Fragment>
      {confirmationModal && (
        <ConfirmationModal
          contentText='Are you sure you want delete primary record?'
          yesButtonText='Yes, delete'
          onYes={handleDelete}
          onCancel={() => setConfirmationModal(null)}
          visible={!!confirmationModal}
        />
      )}
      {addressModal && (
        <AddressModal
          isOpen={!!addressModal}
          onClose={() => setAddressModal(null)}
          addressees={addresses}
          handleChange={handleChangeAddress}
          countries={countries}
          data={addressModal}
        />
      )}
      {phoneModal && (
        <PhoneModal
          isOpen={!!phoneModal}
          onClose={() => setPhoneModal(null)}
          phoneNumbers={phoneNumbers}
          handleChange={handleChangePhone}
          countries={countries}
          data={phoneModal}
          clientId={clientDetails.id}
        />
      )}
      {emailModal && (
        <EmailModal
          isOpen={!!emailModal}
          onClose={() => setEmailModal(null)}
          emails={emails}
          handleChange={handleChangeEmail}
          data={emailModal}
        />
      )}
      <ClientNavigation
        isEdit={isEdit}
        data={clientDetails}
        tab={ClientTab.CONTACT}
        updateClientStatus={updateClientStatus}
      />
      <MainTemplate>
        <DataGrid
          rightAddon={
            <ButtonGroup>
              {!isEdit ? (
                <Button
                  type='button'
                  onClick={() => setEdit(true)}
                  disabled={
                    !hasPermission(permissions, PermissionsEnum.CanEditClient) || isSubmitting
                  }
                  data-test='edit-button'
                >
                  Edit
                </Button>
              ) : (
                <React.Fragment>
                  <Button
                    variant='outline'
                    type='button'
                    onClick={cancel}
                    disabled={isSubmitting}
                    data-test='cancel-button'
                  >
                    Cancel
                  </Button>
                  <Button
                    key='button'
                    type='button'
                    onClick={handleSubmitForm}
                    disabled={isSubmitting}
                    data-test='save-button'
                  >
                    Save
                    {isSubmitting && <Spinner data-icon='inline-start' />}
                  </Button>
                </React.Fragment>
              )}
            </ButtonGroup>
          }
        >
          <DataColumn colSpan={2}>
            <DataCard title='Addresses'>
              <div className='flex flex-col gap-4'>
                <div className='flex justify-end'>
                  <AddButton
                    text='Add'
                    disabled={!isEdit || !defaultCountry}
                    onClick={() =>
                      setAddressModal({
                        id: undefined,
                        isPrimary: false,
                        tempId: uuid(),
                        streetHouseNo: null,
                        country: defaultCountry,
                        postalCode: null,
                        state: null,
                        street: null,
                        type: 'Home',
                        city: null,
                      })
                    }
                  />
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className='w-24 text-center'>Primary</TableHead>
                      <TableHead>Address Line 1</TableHead>
                      <TableHead>Street Nr.</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Postal Code</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className='w-16' />
                    </TableRow>
                  </TableHeader>
                  <TableBody>{renderAddresses()}</TableBody>
                </Table>
              </div>
            </DataCard>
          </DataColumn>

          <DataCard title='Phone Numbers'>
            <div className='flex flex-col gap-4'>
              <div className='flex justify-end'>
                <AddButton
                  text='Add'
                  disabled={!isEdit}
                  onClick={() => {
                    setFieldValue('newCountryCode', undefined)
                    setPhoneModal({
                      id: undefined,
                      tempId: uuid(),
                      countryCode: '',
                      number: '',
                      type: 'Home',
                      isPrimary: false,
                      isVerified: false,
                      isUsedIn2FA: false,
                    })
                  }}
                />
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className='w-24 text-center'>Primary</TableHead>
                    <TableHead>Country Code</TableHead>
                    <TableHead>Number</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className='text-center'>Verified</TableHead>
                    <TableHead className='text-center'>2FA</TableHead>
                    <TableHead className='w-16' />
                  </TableRow>
                </TableHeader>
                <TableBody>{renderPhoneNumbers()}</TableBody>
              </Table>
            </div>
          </DataCard>

          <DataCard title='Emails'>
            <div className='flex flex-col gap-4'>
              <div className='flex justify-end'>
                <AddButton
                  text='Add'
                  disabled={!isEdit}
                  onClick={() =>
                    setEmailModal({
                      id: undefined,
                      tempId: uuid(),
                      isPrimary: false,
                      emailType: 'Personal',
                      address: '',
                      isUsedIn2FA: false,
                    })
                  }
                />
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className='w-24 text-center'>Primary</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className='text-center'>2FA</TableHead>
                    <TableHead className='w-16' />
                  </TableRow>
                </TableHeader>
                <TableBody>{renderEmails()}</TableBody>
              </Table>
            </div>
          </DataCard>
        </DataGrid>
      </MainTemplate>
    </React.Fragment>
  )
}

export type ClientDetailsOtherFormValues = ClientDetailedDto

interface OuterProps {
  clientDetails: ClientDetailedDto
  onSubmit(values: FormSubmitValues<ClientDetailsOtherFormValues>): Promise<void>
  updateClientStatus(values: UpdateClientStatus): void
  countries: CountryDto[]
  permissions: PermissionDto[]
}

function dataToSubmit(
  values: ClientDetailedDto,
  props: OuterProps
): FormSubmitValues<ClientDetailedDto> {
  return {
    ...values,
    phoneNumbers: values.phoneNumbers.map((x) => {
      const found = props.clientDetails.phoneNumbers.find(
        (y) => y.countryCode === x.countryCode && y.number === x.number
      )
      if (found) return { ...x, id: found.id }
      return x
    }),
  }
}

export const ClientDetailsContactForm = withFormik<OuterProps, ClientDetailsOtherFormValues>({
  mapPropsToValues: ({ clientDetails }) => {
    const currentClientDetails: ClientDetailedDto = clientDetails || {}
    return {
      ...currentClientDetails,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values, props))
    } finally {
      setSubmitting(false)
    }
  },
  enableReinitialize: true,
})(ClientContactFormUI)
